import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

import { Container, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import CompletionStatusReportFilters from './components/CompletionStatusReportFilters';
import CompletionStatusReportDataTable, {
    type OpenDetailsPayload,
} from './components/CompletionStatusReportDataTable';
import CompletionStatusReportModal from '@/src/pages/exams/examAssignments/components/CompletionStatusReportModal';
import type { CompletionStatusReportSummaryDTO } from './models/completionStatusReportDTOs';
import type { CompletionStatusReportFilterState } from './models/completionStatusReportFilterDTO';
import { useAuth } from '@/src/app/context/useAuth';

// Completion Status Report page: fetch, filter, and display the completion status report

const CompletionStatusReportPage: React.FC = () => {
    document.title = 'Completion Status Report';
    // Page state
    const [data, setData] = useState<Array<CompletionStatusReportSummaryDTO>>([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [examName, setExamName] = useState(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) return JSON.parse(saved)?.examName || '';
        } catch {
            // intentionally empty
        }
        return '';
    });
    const pendingExamNameRef = useRef('');

    const [filterState, setFilterState] = useState<CompletionStatusReportFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    groupBy:
                        parsed.groupBy === 'examClass'
                            ? 'assessmentClass'
                            : parsed.groupBy === 'exam'
                              ? 'assessment'
                              : parsed.groupBy || 'assessmentClass',
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examId:
                        parsed.examId ||
                        (parsed.examIds && parsed.examIds.length > 0 ? parsed.examIds[0] : 0),
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            groupBy: 'assessmentClass',
            examTypeMasterId: 0,
            examId: 0,
        };
    });

    const [appliedFilters, setAppliedFilters] =
        useState<CompletionStatusReportFilterState>(filterState);

    // Details modal state — set by row click on the summary data table.
    const [detailsPayload, setDetailsPayload] = useState<OpenDetailsPayload | null>(null);

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                groupBy: filterState.groupBy,
                examTypeMasterId: filterState.examTypeMasterId,
                examId: filterState.examId,
                examIds: [filterState.examId],
                examName,
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, examName, userId, UNIVERSAL_FILTER_KEY]);

    // Load table data using current or provided filters
    const refreshTableData = useCallback(
        async (filters: CompletionStatusReportFilterState = filterState) => {
            const {
                gradeLevelMasterId,
                districtId,
                schoolId,
                classId,
                groupBy,
                examTypeMasterId,
                examId,
            } = filters;

            setHasSearched(true);
            setError('');
            setLoaded(false);

            if (!districtId || !schoolId || !gradeLevelMasterId) {
                setError('Please select District, School, and Grade.');
                setLoaded(true);
                return;
            }

            let requestUrl = `reports/completionStatusReport?districtId=${districtId}`;
            requestUrl += `&schoolId=${schoolId}`;
            requestUrl += `&gradeId=${gradeLevelMasterId}`;
            requestUrl += `&classId=${classId}`;
            requestUrl += `&groupBy=${groupBy}`;
            requestUrl += `&examTypeId=${examTypeMasterId}`;
            requestUrl += `&examId=${examId}`;

            try {
                const res = await apiRequest<Array<CompletionStatusReportSummaryDTO>>(
                    'GET',
                    requestUrl
                );
                setData(res.data ?? []);
                setAppliedFilters(filters);
                if (pendingExamNameRef.current) setExamName(pendingExamNameRef.current);
            } catch {
                setError('Failed to load Completion Status Report');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    useEffect(() => {
        const fs = filterState;
        if (fs.districtId && fs.schoolId && fs.gradeLevelMasterId) {
            refreshTableData(fs);
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Render page
    return (
        <Container>
            <div>
                <div>
                    <h1 className="page-header">Completion Status Report</h1>
                    <br />

                    <div>
                        <Box>
                            <CompletionStatusReportFilters
                                filterState={filterState}
                                setFilterState={setFilterState}
                                refreshTableData={refreshTableData}
                                onExamNameChange={(name) => {
                                    pendingExamNameRef.current = name;
                                }}
                            />
                        </Box>
                    </div>

                    {examName && (
                        <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '8px' }}>
                            {examName}
                        </h2>
                    )}

                    <Loading
                        visible={hasSearched && !loaded}
                        message="Loading Completion Status Report..."
                    />
                    <ErrorMessage error={hasSearched ? error : ''} />

                    {hasSearched && loaded && (
                        <div>
                            <div>
                                <CompletionStatusReportDataTable
                                    data={data}
                                    groupBy={appliedFilters.groupBy}
                                    selectedClassId={appliedFilters.classId}
                                    onOpenDetails={(payload) => setDetailsPayload(payload)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {detailsPayload && (
                <CompletionStatusReportModal
                    open
                    examAssignmentId={detailsPayload.examAssignmentId}
                    examName={detailsPayload.examName ?? ''}
                    className={detailsPayload.className}
                    classId={detailsPayload.classId}
                    isByExam={detailsPayload.isByExam}
                    examId={detailsPayload.examId}
                    onClose={() => setDetailsPayload(null)}
                />
            )}
        </Container>
    );
};

export default CompletionStatusReportPage;
